import React, { useEffect, useCallback } from 'react';

export interface DialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Dialog title */
  title: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Optional icon component to show in header */
  icon?: React.ReactNode;
  /** Header gradient color (tailwind classes) */
  headerColor?: string;
  /** Dialog content */
  children: React.ReactNode;
  /** Footer content (buttons, etc.) */
  footer?: React.ReactNode;
  /** Maximum width of dialog */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  /** Whether to close on backdrop click */
  closeOnBackdropClick?: boolean;
  /** Whether to close on Escape key */
  closeOnEscape?: boolean;
  /** Custom class name for the dialog container */
  className?: string;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
}

const MAX_WIDTH_CLASSES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  full: 'sm:max-w-full sm:mx-4',
};

const DEFAULT_HEADER_COLORS: Record<string, string> = {
  blue: 'from-blue-500 to-blue-600',
  green: 'from-green-500 to-green-600',
  red: 'from-red-500 to-red-600',
  yellow: 'from-yellow-500 to-yellow-600',
  purple: 'from-purple-500 to-purple-600',
  indigo: 'from-indigo-500 to-indigo-600',
  gray: 'from-gray-500 to-gray-600',
};

/**
 * Dialog - A reusable modal dialog component
 *
 * Provides a consistent dialog structure with:
 * - Gradient header with optional icon
 * - Scrollable content area
 * - Fixed footer for action buttons
 * - Mobile-responsive (bottom sheet on mobile, centered on desktop)
 * - Escape key and backdrop click to close
 *
 * @example
 * ```tsx
 * <Dialog
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 *   title="Create Order"
 *   subtitle="Order #12345"
 *   headerColor="blue"
 *   icon={<DocumentIcon />}
 *   footer={
 *     <>
 *       <Button onClick={onCancel}>Cancel</Button>
 *       <Button onClick={onSubmit} variant="primary">Submit</Button>
 *     </>
 *   }
 * >
 *   {form content}
 * </Dialog>
 * ```
 */
const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  headerColor = 'blue',
  children,
  footer,
  maxWidth = '5xl',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  showCloseButton = true,
}) => {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Resolve header gradient
  const headerGradient = DEFAULT_HEADER_COLORS[headerColor] || headerColor;
  const maxWidthClass = MAX_WIDTH_CLASSES[maxWidth];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl w-full ${maxWidthClass} max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col ${className}`}
      >
        {/* Header */}
        <div
          className={`bg-gradient-to-r ${headerGradient} px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0`}
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {icon && (
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">{title}</h2>
              {subtitle && (
                <p className="text-white/80 text-xs sm:text-sm truncate">{subtitle}</p>
              )}
            </div>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="bg-gray-50 dark:bg-gray-700 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 pb-safe">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;

/**
 * DialogButton - Consistent button styling for dialog footers
 */
export interface DialogButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
  className?: string;
}

const BUTTON_VARIANTS = {
  primary: 'text-white bg-blue-500 hover:bg-blue-600',
  secondary: 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
  danger: 'text-white bg-red-500 hover:bg-red-600',
};

export const DialogButton: React.FC<DialogButtonProps> = ({
  children,
  onClick,
  variant = 'secondary',
  disabled = false,
  loading = false,
  type = 'button',
  className = '',
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px] ${BUTTON_VARIANTS[variant]} ${className}`}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};
